import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Select from "react-select";
import "./ApplyForm.css";

const courseOptions = [
  { value: "Playgroup", label: "Playgroup" },
  { value: "Nursery", label: "Nursery" },
  { value: "Prep", label: "Prep" },
  { value: "One", label: "One" },
  { value: "Two", label: "Two" },
  { value: "Three", label: "Three" },
  { value: "Four", label: "Four" },
  { value: "Five", label: "Five" },
  { value: "Six", label: "Six" },
  { value: "Seven", label: "Seven" },
  { value: "Eight", label: "Eight" },
  { value: "Nine", label: "Nine" },
  { value: "Tenth", label: "Ten" },
  { value: "1st Year", label: "1st Year" },
  { value: "2nd Year", label: "2nd Year" },
];

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const initialForm = {
  name: "",
  email: "",
  phone: "",
  course: "",
  message: "",
};

const validate = (data) => {
  const errs = {};

  const name = data.name.trim();
  if (!name) {
    errs.name = "Name is required";
  } else if (name.length < 3) {
    errs.name = "Name must be at least 3 characters";
  }

  const email = data.email.trim();
  if (!email) {
    errs.email = "Email is required";
  } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
    errs.email = "Invalid email address";
  }

  if (!data.phone) {
    errs.phone = "Phone number is required";
  } else if (!/^03\d{9}$/.test(data.phone)) {
    errs.phone = "Phone must be in format 03XXXXXXXXX (11 digits)";
  }

  if (!data.course) {
    errs.course = "Please select a course";
  }

  return errs;
};

function ApplyForm() {
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    const cleaned = name === "phone" ? value.replace(/\D/g, "") : value;
    const next = { ...formData, [name]: cleaned };

    setFormData(next);
    if (submitted) setErrors(validate(next));
  };

  const handleCourseChange = (selectedOption) => {
    const next = { ...formData, course: selectedOption ? selectedOption.value : "" };

    setFormData(next);
    if (submitted) setErrors(validate(next));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setSubmitted(true);
    setStatusMessage("");

    const formErrors = validate(formData);
    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      setStatusMessage("⚠️ Please fix the errors above and try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "admissions"), {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone,
        course: formData.course,
        message: formData.message.trim(),
        createdAt: serverTimestamp(),
      });

      setStatusMessage(
        "✅ Your application has been submitted successfully! Our team will contact you shortly."
      );
      setFormData(initialForm);
      setErrors({});
      setSubmitted(false);
    } catch (error) {
      console.error("Error adding document: ", error);
      setStatusMessage("❌ Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const showError = (field) => submitted && errors[field];

  return (
    <div className="apply-page">
      <header className="apply-hero">
        <div className="hero-inner">
          <span className="hero-badge">Admissions Open</span>
          <h1>Admission Application</h1>
          <p>
            Complete your application by filling in the details below.
            Fields marked <span className="req">*</span> are required.
          </p>
        </div>
      </header>

      <main className="apply-container">
        <div className="apply-card">
          {statusMessage && (
            <div
              role="status"
              className={`status-message ${
                statusMessage.startsWith("✅") ? "success" : "error"
              }`}
            >
              {statusMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="apply-form" noValidate autoComplete="off">
            {/* Section 1 */}
            <section className="form-section">
              <div className="section-title">
                <span className="section-num">01</span>
                <h3>Student Information</h3>
              </div>

              <div className="form-grid">
                <div className={`field ${showError("name") ? "has-error" : ""}`}>
                  <label htmlFor="name">
                    Full Name <span className="req">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="e.g. Farrukh Saleem"
                    value={formData.name}
                    onChange={handleChange}
                    aria-invalid={showError("name") ? "true" : "false"}
                    aria-describedby={showError("name") ? "name-error" : undefined}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  {showError("name") && (
                    <span className="error" id="name-error" role="alert">
                      {errors.name}
                    </span>
                  )}
                </div>

                <div className={`field ${showError("email") ? "has-error" : ""}`}>
                  <label htmlFor="email">
                    Email Address <span className="req">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    aria-invalid={showError("email") ? "true" : "false"}
                    aria-describedby={showError("email") ? "email-error" : undefined}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    inputMode="email"
                  />
                  {showError("email") && (
                    <span className="error" id="email-error" role="alert">
                      {errors.email}
                    </span>
                  )}
                </div>

                <div className={`field full ${showError("phone") ? "has-error" : ""}`}>
                  <label htmlFor="phone">
                    Phone Number <span className="req">*</span>
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="03XXXXXXXXX"
                    inputMode="numeric"
                    maxLength={11}
                    value={formData.phone}
                    onChange={handleChange}
                    aria-invalid={showError("phone") ? "true" : "false"}
                    aria-describedby={showError("phone") ? "phone-error" : undefined}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  {showError("phone") && (
                    <span className="error" id="phone-error" role="alert">
                      {errors.phone}
                    </span>
                  )}
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section className="form-section">
              <div className="section-title">
                <span className="section-num">02</span>
                <h3>Class Selection</h3>
              </div>

              <div className={`field full ${showError("course") ? "has-error" : ""}`}>
                <label htmlFor="course">
                  Class <span className="req">*</span>
                </label>
                <Select
                  inputId="course"
                  options={courseOptions}
                  placeholder="Select Class..."
                  value={courseOptions.find((opt) => opt.value === formData.course) || null}
                  onChange={handleCourseChange}
                  isClearable
                  isSearchable={false}
                  classNamePrefix="react-select"
                  aria-label="Course selection"
                  aria-describedby={showError("course") ? "course-error" : undefined}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="absolute"
                  menuPlacement="auto"
                  maxMenuHeight={240}
                  menuShouldScrollIntoView={false}
                  blurInputOnSelect={true}
                  styles={selectStyles}
                />
                {showError("course") && (
                  <span className="error" id="course-error" role="alert">
                    {errors.course}
                  </span>
                )}
              </div>
            </section>

            {/* Section 3 */}
            <section className="form-section">
              <div className="section-title">
                <span className="section-num">03</span>
                <h3>Additional Information</h3>
              </div>

              <div className="field full">
                <label htmlFor="message">Message (optional)</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Any additional information you would like to share..."
                  value={formData.message}
                  onChange={handleChange}
                  rows="3"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
              </div>
            </section>

            <div className="form-note">
              <span aria-hidden="true">🔒</span>
              <span>
                Your information is kept safe and will be used only for the admission process.
              </span>
            </div>

            <div className="submit-wrap">
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading && <span className="btn-spinner" aria-hidden="true" />}
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>

        <p className="apply-footer-note">
          Once you submit, our team will get in touch with you shortly.
        </p>
      </main>
    </div>
  );
}

export default ApplyForm;